<template>
  <div>
    <h3 class="mb-2">
      Validação de Documentos
    </h3>

    <div
      v-if="loading"
      class="text-center my-3"
    >
      <b-spinner
        variant="primary"
        label="Carregando"
      />
    </div>

    <template v-else>
      <template v-for="document in DOCUMENTS">
        <document-validation-card
          v-if="settings[document.type]"
          :key="document.type"
          v-model="settings[document.type]"
          :title="document.title"
          :subtitle="document.subtitle"
          :disabled="!canManage || saving"
        />
      </template>

      <div
        v-if="canManage"
        class="d-flex justify-content-end"
      >
        <b-button
          variant="outline-secondary"
          class="mr-1"
          :disabled="saving || !hasChanges"
          @click="cancel"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !hasChanges"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-50"
          />
          Salvar Alterações
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { cloneDeep, isEqual } from 'lodash'
import documentValidationStoreModule from './documentValidationStoreModule'
import DocumentValidationCard from './DocumentValidationCard.vue'

const DOCUMENTS = [
  { type: 'cpf', title: 'CPF', subtitle: 'Regras aplicadas na validação do CPF no cadastro de profissionais' },
  { type: 'cnpj', title: 'CNPJ', subtitle: 'Regras aplicadas na validação do CNPJ no cadastro de empresas' },
]

// A API devolve uma lista com um item por document_type; a tela trabalha com { cpf: {...}, cnpj: {...} }
const toSettingsMap = list => (list || []).reduce((acc, setting) => ({
  ...acc,
  [setting.document_type]: {
    enabled: setting.enabled,
    provider_order: setting.provider_order,
    on_unavailable: setting.on_unavailable,
  },
}), {})

export default {
  components: {
    BButton,
    BSpinner,
    DocumentValidationCard,
  },
  computed: {
    canManage() {
      return this.$can('manage', 'DocumentValidation')
    },
  },
  setup() {
    const toast = useToast()
    const MODULE_NAME = 'app-document-validation'

    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, documentValidationStoreModule)
    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    const loading = ref(true)
    const saving = ref(false)
    const settings = ref({})
    const savedSettings = ref({})

    const hasChanges = computed(() => !isEqual(settings.value, savedSettings.value))

    const showToast = (title, variant, icon) => {
      toast({
        component: ToastificationContent,
        props: { title, variant, icon },
      })
    }

    const fetchSettings = () => {
      loading.value = true
      store.dispatch(`${MODULE_NAME}/fetchSettings`)
        .then(response => {
          savedSettings.value = toSettingsMap(response.data)
          settings.value = cloneDeep(savedSettings.value)
        })
        .catch(() => showToast('Erro ao carregar configurações de validação', 'danger', 'AlertTriangleIcon'))
        .finally(() => {
          loading.value = false
        })
    }
    fetchSettings()

    // O backend salva um documento por vez, então só envia os que mudaram
    const save = () => {
      const changedTypes = Object.keys(settings.value)
        .filter(type => !isEqual(settings.value[type], savedSettings.value[type]))

      saving.value = true
      Promise.all(changedTypes.map(documentType => store.dispatch(`${MODULE_NAME}/updateSetting`, {
        documentType,
        ...settings.value[documentType],
      })))
        .then(() => {
          savedSettings.value = cloneDeep(settings.value)
          showToast('Configurações salvas com sucesso', 'success', 'CheckIcon')
        })
        .catch(() => {
          showToast('Erro ao salvar configurações de validação', 'danger', 'AlertTriangleIcon')
          // Parte pode ter sido salva: recarrega para a tela refletir o que está no backend
          fetchSettings()
        })
        .finally(() => {
          saving.value = false
        })
    }

    const cancel = () => {
      settings.value = cloneDeep(savedSettings.value)
    }

    return {
      DOCUMENTS,
      loading,
      saving,
      settings,
      hasChanges,
      save,
      cancel,
    }
  },
}
</script>
